import React, { useState, useEffect, useMemo } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  getTeam,
  updateTeam,
  deleteTeam,
  inviteTeamMember,
  reactivateTeamMember,
  removeTeamMember,
  revokeInvitation,
  updateMemberRoles,
  getRoles,
  getMemberPermissions,
  getTeamActivity,
  TeamDetails as TeamDetailsType,
  TeamMember,
  Role,
  MemberPermissions,
  ActivityLog,
} from '../../services/api/team.service';
import { toast } from 'react-toastify';
import {
  ArrowLeft,
  Edit,
  Trash2,
  UserPlus,
  X,
  Shield,
  Save,
  History,
  Eye,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { useArtist } from '../../contexts/ArtistContext';
import { useUser } from '../../contexts/UserContext';
import { trackEvent, trackActivationMilestone, TEAM_EVENTS, ACTIVATION_EVENTS } from '../../services/analytics';
import { auth } from '../../config/firebase';

// Component to render action-based summary of activity details
const ActivityDetails: React.FC<{ log: ActivityLog }> = ({ log }) => {
  const { details, actionType, targetUserName, userName } = log;

  // Helper to parse details if it's a JSON string
  const parseDetails = (details: any): Record<string, any> => {
    if (!details) {
      return {};
    }
    if (typeof details === 'string') {
      // Check if it's an empty string
      if (details.trim() === '') {
        return {};
      }
      try {
        const parsed = JSON.parse(details);
        return typeof parsed === 'object' && parsed !== null ? parsed : { message: details };
      } catch {
        // If parsing fails, treat as plain string
        return { message: details };
      }
    }
    return typeof details === 'object' && details !== null ? details : {};
  };

  // Parse details first
  const parsedDetails = parseDetails(details);

  // Check if parsed details is empty
  if (!parsedDetails || Object.keys(parsedDetails).length === 0) {
    return <span className="text-gray-400 text-sm">-</span>;
  }

  // Helper to create a human-readable summary from any object
  const createSummary = (obj: Record<string, any>, maxDepth: number = 2, currentDepth: number = 0): string => {
    if (currentDepth >= maxDepth) {
      return '...';
    }

    const summaryParts: string[] = [];
    const keys = Object.keys(obj).slice(0, 5); // Limit to first 5 keys

    keys.forEach((key) => {
      const value = obj[key];
      const formattedKey = key
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      if (value === null || value === undefined) {
        return; // Skip null/undefined
      } else if (typeof value === 'string' && value.length > 0) {
        // Truncate long strings
        const displayValue = value.length > 30 ? value.substring(0, 30) + '...' : value;
        summaryParts.push(`${formattedKey}: ${displayValue}`);
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        summaryParts.push(`${formattedKey}: ${value}`);
      } else if (Array.isArray(value)) {
        if (value.length > 0) {
          const arraySummary = value.length <= 3 
            ? value.map(v => typeof v === 'string' ? v : typeof v === 'object' ? '...' : String(v)).join(', ')
            : `${value.slice(0, 2).map(v => typeof v === 'string' ? v : typeof v === 'object' ? '...' : String(v)).join(', ')} +${value.length - 2} more`;
          summaryParts.push(`${formattedKey}: ${arraySummary}`);
        }
      } else if (typeof value === 'object') {
        const nestedSummary = createSummary(value, maxDepth, currentDepth + 1);
        if (nestedSummary) {
          summaryParts.push(`${formattedKey}: ${nestedSummary}`);
        }
      }
    });

    if (Object.keys(obj).length > keys.length) {
      summaryParts.push(`+${Object.keys(obj).length - keys.length} more`);
    }

    return summaryParts.join('; ');
  };

  // Helper to format role names (capitalize first letter)
  const formatRole = (role: string): string => {
    return role
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Helper to format role list as compact text
  const formatRoleList = (roles: string[]): string => {
    if (roles.length === 0) return '';
    if (roles.length === 1) return formatRole(roles[0]);
    if (roles.length <= 3) return roles.map(formatRole).join(', ');
    return `${roles.slice(0, 2).map(formatRole).join(', ')} +${roles.length - 2} more`;
  };

  // Generate action-based summary
  // Normalize action type to handle variations
  const normalizedActionType = actionType.toLowerCase().replace(/\s+/g, '_');
  
  switch (normalizedActionType) {
    case 'member_added':
      const addedRoles = parsedDetails.roles || parsedDetails.new_roles || [];
      if (addedRoles.length > 0) {
        return (
          <span className="text-sm text-gray-700">
            Assigned roles: <span className="font-medium text-green-600">{formatRoleList(addedRoles)}</span>
          </span>
        );
      }
      return <span className="text-sm text-gray-700">Added to team</span>;

    case 'invite_sent':
      const inviteRoles = parsedDetails.roles || parsedDetails.new_roles || [];
      const inviteEmail = parsedDetails.email || '';
      if (inviteRoles.length > 0) {
        return (
          <span className="text-sm text-gray-700">
            Invited with {inviteRoles.length === 1 ? 'role' : 'roles'}: <span className="font-medium">{formatRoleList(inviteRoles)}</span>
            {inviteEmail && <span className="text-gray-500 text-xs ml-1">({inviteEmail})</span>}
          </span>
        );
      }
      return (
        <span className="text-sm text-gray-700">
          Invitation sent{inviteEmail && <span className="text-gray-500 text-xs ml-1">to {inviteEmail}</span>}
        </span>
      );

    case 'member_removed':
      const removedReason = parsedDetails.reason || '';
      return (
        <span className="text-sm text-gray-700">
          Removed from team{removedReason && <span className="text-gray-500 text-xs ml-1">({removedReason})</span>}
        </span>
      );

    case 'role_assigned':
    case 'role_updated':
    case 'roles_updated':
      const addedRolesList = parsedDetails.addedRoles || [];
      const removedRolesList = parsedDetails.removedRoles || [];
      const oldRoles = parsedDetails.old_roles || parsedDetails.previousRoles || [];
      const newRoles = parsedDetails.new_roles || parsedDetails.roles || [];

      // Create meaningful summary based on what changed
      if (addedRolesList.length > 0 && removedRolesList.length > 0) {
        return (
          <span className="text-sm text-gray-700">
            <span className="text-green-600 font-medium">+{addedRolesList.length}</span> role{addedRolesList.length !== 1 ? 's' : ''}, 
            <span className="text-red-600 font-medium"> -{removedRolesList.length}</span> role{removedRolesList.length !== 1 ? 's' : ''}
            <span className="text-gray-500 text-xs ml-1">
              ({formatRoleList(addedRolesList)} / {formatRoleList(removedRolesList)})
            </span>
          </span>
        );
      } else if (addedRolesList.length > 0) {
        return (
          <span className="text-sm text-gray-700">
            Added role{addedRolesList.length !== 1 ? 's' : ''}: <span className="font-medium text-green-600">{formatRoleList(addedRolesList)}</span>
          </span>
        );
      } else if (removedRolesList.length > 0) {
        return (
          <span className="text-sm text-gray-700">
            Removed role{removedRolesList.length !== 1 ? 's' : ''}: <span className="font-medium text-red-600">{formatRoleList(removedRolesList)}</span>
          </span>
        );
      } else if (oldRoles.length > 0 || newRoles.length > 0) {
        return (
          <span className="text-sm text-gray-700">
            Changed to: <span className="font-medium">{formatRoleList(newRoles)}</span>
            {oldRoles.length > 0 && (
              <span className="text-gray-500 text-xs ml-1">(was: {formatRoleList(oldRoles)})</span>
            )}
          </span>
        );
      }
      return <span className="text-sm text-gray-700">Roles modified</span>;

    case 'team_updated':
      const teamChanges: string[] = [];
      if (parsedDetails.old_values && parsedDetails.new_values) {
        Object.keys(parsedDetails.new_values).forEach((key) => {
          const oldVal = parsedDetails.old_values[key];
          const newVal = parsedDetails.new_values[key];
          if (oldVal !== newVal && newVal !== null && newVal !== undefined) {
            const formattedKey = key
              .split('_')
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ');
            teamChanges.push(formattedKey);
          }
        });
      }
      if (teamChanges.length > 0) {
        return (
          <span className="text-sm text-gray-700">
            Updated {teamChanges.length === 1 ? 'field' : 'fields'}: <span className="font-medium">{teamChanges.join(', ')}</span>
          </span>
        );
      }
      return <span className="text-sm text-gray-700">Team settings changed</span>;

    case 'team_created':
      const teamName = parsedDetails.team_name || parsedDetails.name || '';
      return (
        <span className="text-sm text-gray-700">
          Team created{teamName && <span className="font-medium ml-1">"{teamName}"</span>}
        </span>
      );

    case 'team_deleted':
      return <span className="text-sm text-gray-700">Team deleted</span>;

    case 'permission_updated':
    case 'permissions_updated':
      const oldPerms = parsedDetails.old_permissions || parsedDetails.previousPermissions || [];
      const newPerms = parsedDetails.new_permissions || parsedDetails.permissions || [];
      const addedPerms = parsedDetails.added_permissions || [];
      const removedPerms = parsedDetails.removed_permissions || [];

      if (addedPerms.length > 0 && removedPerms.length > 0) {
        return (
          <span className="text-sm text-gray-700">
            <span className="text-green-600 font-medium">+{addedPerms.length}</span> permission{addedPerms.length !== 1 ? 's' : ''}, 
            <span className="text-red-600 font-medium"> -{removedPerms.length}</span> permission{removedPerms.length !== 1 ? 's' : ''}
          </span>
        );
      } else if (addedPerms.length > 0) {
        return (
          <span className="text-sm text-gray-700">
            Granted <span className="font-medium text-green-600">{addedPerms.length}</span> permission{addedPerms.length !== 1 ? 's' : ''}
          </span>
        );
      } else if (removedPerms.length > 0) {
        return (
          <span className="text-sm text-gray-700">
            Revoked <span className="font-medium text-red-600">{removedPerms.length}</span> permission{removedPerms.length !== 1 ? 's' : ''}
          </span>
        );
      }
      return (
        <span className="text-sm text-gray-700">
          Permissions: <span className="font-medium">{oldPerms.length} → {newPerms.length}</span>
        </span>
      );

    case 'invite_accepted':
    case 'invitation_accepted':
      const acceptedRoles = parsedDetails.roles || [];
      // For accepted invitations, try to get email from various possible locations
      const acceptedEmail = parsedDetails.email || parsedDetails.target_email || parsedDetails.user_email || '';
      // The user who accepted is typically in userName field
      const acceptedUser = userName || targetUserName || '';
      
      // Build meaningful summary - prioritize email, then user name
      if (acceptedEmail) {
        if (acceptedRoles.length > 0) {
          return (
            <span className="text-sm text-gray-700">
              User <span className="font-medium">({acceptedEmail})</span> accepted the invitation with roles: <span className="font-medium text-green-600">{formatRoleList(acceptedRoles)}</span>
            </span>
          );
        }
        return (
          <span className="text-sm text-gray-700">
            User <span className="font-medium">({acceptedEmail})</span> accepted the invitation
          </span>
        );
      } else if (acceptedUser) {
        // If no email but we have user name, show that with roles
        if (acceptedRoles.length > 0) {
          return (
            <span className="text-sm text-gray-700">
              <span className="font-medium">{acceptedUser}</span> accepted the invitation with roles: <span className="font-medium text-green-600">{formatRoleList(acceptedRoles)}</span>
            </span>
          );
        }
        return (
          <span className="text-sm text-gray-700">
            <span className="font-medium">{acceptedUser}</span> accepted the invitation
          </span>
        );
      }
      // Fallback if no user info available - still show roles if available
      if (acceptedRoles.length > 0) {
        return (
          <span className="text-sm text-gray-700">
            Accepted invitation with roles: <span className="font-medium text-green-600">{formatRoleList(acceptedRoles)}</span>
          </span>
        );
      }
      return <span className="text-sm text-gray-700">Accepted team invitation</span>;

    case 'invite_rejected':
    case 'invitation_rejected':
      const rejectedEmail = parsedDetails.email || parsedDetails.target_email || parsedDetails.user_email || '';
      const rejectedUser = userName || targetUserName || '';
      
      if (rejectedEmail) {
        return (
          <span className="text-sm text-gray-700">
            User <span className="font-medium">({rejectedEmail})</span> declined the invitation
          </span>
        );
      } else if (rejectedUser) {
        return (
          <span className="text-sm text-gray-700">
            <span className="font-medium">{rejectedUser}</span> declined the invitation
          </span>
        );
      }
      return <span className="text-sm text-gray-700">Declined team invitation</span>;

    case 'invite_revoked':
      return <span className="text-sm text-gray-700">Invitation cancelled</span>;

    case 'member_reactivated':
      const reactivatedRoles = parsedDetails.roles || [];
      if (reactivatedRoles.length > 0) {
        return (
          <span className="text-sm text-gray-700">
            Reactivated with roles: <span className="font-medium">{formatRoleList(reactivatedRoles)}</span>
          </span>
        );
      }
      return <span className="text-sm text-gray-700">Member reactivated</span>;

    default:
      // Generic fallback: show most important info based on action type
      // Check if this might be an invitation-related action that didn't match above
      const isInvitationAction = normalizedActionType.includes('invitation') || normalizedActionType.includes('invite');
      const roles = parsedDetails.roles || parsedDetails.new_roles || parsedDetails.old_roles || [];
      const email = parsedDetails.email || parsedDetails.target_email || parsedDetails.user_email || '';
      const status = parsedDetails.status || '';
      const message = parsedDetails.message || '';
      const defaultUser = userName || targetUserName || '';
      
      // For invitation-related actions, try to create meaningful summary
      if (isInvitationAction) {
        if (normalizedActionType.includes('accept')) {
          if (email && roles.length > 0) {
            return (
              <span className="text-sm text-gray-700">
                User <span className="font-medium">({email})</span> accepted the invitation with roles: <span className="font-medium text-green-600">{formatRoleList(roles)}</span>
              </span>
            );
          } else if (email) {
            return (
              <span className="text-sm text-gray-700">
                User <span className="font-medium">({email})</span> accepted the invitation
              </span>
            );
          } else if (defaultUser && roles.length > 0) {
            return (
              <span className="text-sm text-gray-700">
                <span className="font-medium">{defaultUser}</span> accepted the invitation with roles: <span className="font-medium text-green-600">{formatRoleList(roles)}</span>
              </span>
            );
          } else if (defaultUser) {
            return (
              <span className="text-sm text-gray-700">
                <span className="font-medium">{defaultUser}</span> accepted the invitation
              </span>
            );
          } else if (roles.length > 0) {
            return (
              <span className="text-sm text-gray-700">
                Accepted invitation with roles: <span className="font-medium text-green-600">{formatRoleList(roles)}</span>
              </span>
            );
          }
        } else if (normalizedActionType.includes('reject') || normalizedActionType.includes('decline')) {
          if (email) {
            return (
              <span className="text-sm text-gray-700">
                User <span className="font-medium">({email})</span> declined the invitation
              </span>
            );
          } else if (defaultUser) {
            return (
              <span className="text-sm text-gray-700">
                <span className="font-medium">{defaultUser}</span> declined the invitation
              </span>
            );
          }
        }
      }
      
      // For other actions, show roles if available
      if (roles.length > 0) {
        return (
          <span className="text-sm text-gray-700">
            Roles: <span className="font-medium">{formatRoleList(roles)}</span>
          </span>
        );
      }
      if (email) {
        return <span className="text-sm text-gray-700">Email: <span className="font-medium">{email}</span></span>;
      }
      if (status) {
        return <span className="text-sm text-gray-700">Status: <span className="font-medium">{status}</span></span>;
      }
      if (message) {
        return <span className="text-sm text-gray-700">{message}</span>;
      }
      
      // Last resort: create a smart summary from the details object
      const summary = createSummary(parsedDetails);
      if (summary) {
        return (
          <span className="text-sm text-gray-700" title={summary}>
            {summary.length > 100 ? summary.substring(0, 100) + '...' : summary}
          </span>
        );
      }
      
      // If we can't create a summary, show a generic message
      return <span className="text-sm text-gray-500">Action performed</span>;
  }
};

const TeamDetails: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const [team, setTeam] = useState<TeamDetailsType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<boolean>(false);
  const [editForm, setEditForm] = useState({ teamName: '', description: '' });
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', roles: [] as string[], message: '' });
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [memberRoles, setMemberRoles] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState<'members' | 'activity'>('members');
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activityPage, setActivityPage] = useState(1);
  const [activityTotal, setActivityTotal] = useState(0);
  const [showPermissionsDialog, setShowPermissionsDialog] = useState(false);
  const [memberPermissions, setMemberPermissions] = useState<MemberPermissions | null>(null);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [showReactivationDialog, setShowReactivationDialog] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [removingMemberId, setRemovingMemberId] = useState<number | null>(null);
  const [revokingInvitationId, setRevokingInvitationId] = useState<number | null>(null);
  const [deletingTeam, setDeletingTeam] = useState(false);
  const [updatingRoles, setUpdatingRoles] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);

  const { hasAnyPermission, hasPermission, currentArtistId, availableArtists } = useArtist();
  const { userProfile } = useUser();

  // Check if user has team permissions or is system admin
  const hasAccess = userProfile?.systemRole === "system_admin" || 
    hasAnyPermission(["team:view", "team:invite", "team:remove", "team:assign_roles", "team:manage"]);

  // Redirect to dashboard if no access
  if (!hasAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check if current user is the team owner
  // Key check: The selected artist must be owned by the logged-in user
  // If user switches to another artist (where they are a team member), buttons should be hidden
  const isTeamOwner = useMemo(() => {
    if (!team || !userProfile || !currentArtistId) {
      console.log('[TeamDetails] Owner check: Missing team, userProfile, or currentArtistId');
      return false;
    }
    
    // Find the selected artist from available artists
    const selectedArtist = availableArtists.find(a => a.artistId === currentArtistId);
    
    // CRITICAL: Only show edit/delete buttons if the selected artist is owned by the logged-in user
    // If user switched to another artist (where they are a team member), isOwner will be false
    const isSelectedArtistOwner = selectedArtist?.isOwner === true;
    
    // Also verify that the team's ownerId matches the logged-in user's ID
    const userId = userProfile.id;
    const ownerId = team.ownerId;
    
    if (userId === undefined || userId === null || ownerId === undefined || ownerId === null) {
      console.log('[TeamDetails] Owner check: Missing userId or ownerId', { userId, ownerId });
      return false;
    }
    
    const userIdNum = Number(userId);
    const ownerIdNum = Number(ownerId);
    const teamOwnerMatches = userIdNum === ownerIdNum;
    
    // User must be:
    // 1. Owner of the selected artist (isSelectedArtistOwner === true)
    // 2. Owner of the team (teamOwnerMatches === true)
    const isOwner = isSelectedArtistOwner && teamOwnerMatches;
    
    // Debug log
    console.log('[TeamDetails] Owner check:', {
      currentArtistId,
      selectedArtistName: selectedArtist?.artistName,
      isSelectedArtistOwner,
      userId: userIdNum,
      ownerId: ownerIdNum,
      teamOwnerMatches,
      isOwner,
      teamName: team.teamName
    });
    
    return isOwner;
  }, [team, userProfile, currentArtistId, availableArtists]);

  // Permission-based visibility controls
  const isSystemAdmin = userProfile?.systemRole === "system_admin";
  
  // Get current user's member record to check their roles
  const currentUserMember = team && userProfile 
    ? team.members.find(m => m.userId === userProfile.id)
    : null;
  
  // Check if current user has the "owner" role (even if they're not the team owner)
  const hasOwnerRole = currentUserMember?.roles?.some(role => role.toLowerCase() === 'owner') || false;
  
  // Filter roles for invite dialog: exclude "owner" role if user is not team owner
  // Team owner can assign any role including "owner", but team members cannot assign "owner"
  const rolesForInvite = isTeamOwner 
    ? availableRoles 
    : availableRoles.filter(role => role.roleCode.toLowerCase() !== 'owner');
  const canViewEmails = isSystemAdmin || hasPermission("team:manage");
  const canViewPermissions = isSystemAdmin || hasPermission("team:manage");
  // Team owner OR team member with "owner" role can manage roles
  const canManageRoles = (isTeamOwner || hasOwnerRole) && (isSystemAdmin || hasPermission("team:assign_roles") || hasPermission("team:manage"));
  // Only team owner can view activity log
  const canViewActivityLog = isTeamOwner && (isSystemAdmin || hasPermission("team:manage"));
  // Team owner OR team member with "owner" role can invite members
  const canInviteMembers = (isTeamOwner || hasOwnerRole) && (isSystemAdmin || hasPermission("team:invite") || hasPermission("team:manage"));
  // Team owner OR team member with "owner" role can remove members
  const canRemoveMembers = (isTeamOwner || hasOwnerRole) && (isSystemAdmin || hasPermission("team:remove") || hasPermission("team:manage"));
  // Only the artist (owner) can edit and delete teams, not team members or system admins
  const canEditTeam = isTeamOwner;
  const canDeleteTeam = isTeamOwner;
  
  // Check if current user is viewing their own member record
  const isCurrentUserMember = (member: TeamMember) => {
    return member.userId === userProfile?.id;
  };

  // Check if a pending invitation has expired
  const isInvitationExpired = (member: TeamMember): boolean => {
    if (member.invitationStatus !== 'pending' || !member.expiresAt) {
      return false;
    }
    try {
      const expirationDate = new Date(member.expiresAt);
      const now = new Date();
      return expirationDate < now;
    } catch {
      return false;
    }
  };

  // Check if current user is a member of this team
  const isCurrentUserTeamMember = team ? team.members.some(m => m.userId === userProfile?.id) : false;
  
  // Calculate accepted members count
  const acceptedMembersCount = team ? team.members.filter(m => m.invitationStatus === 'accepted').length : 0;
  
  // Filter members list based on user role:
  // - Team owner: See all members
  // - Team member with "owner" permission: See all members
  // - Team member without "owner" permission: See only owner and own record
  // - Others: See only owner
  const visibleMembers = team ? team.members.filter((member) => {
    // Always show team owner
    if (member.userId === team.ownerId) {
      return true;
    }
    
    // If current user is team owner, show all members
    if (isTeamOwner) {
      return true;
    }
    
    // If current user is a team member with "owner" permission, show all members
    if (isCurrentUserTeamMember && hasOwnerRole) {
      return true;
    }
    
    // If current user is a team member without "owner" permission, show only their own record
    if (isCurrentUserTeamMember && isCurrentUserMember(member)) {
      return true;
    }
    
    // Otherwise, hide other members
    return false;
  }) : [];

  useEffect(() => {
    if (teamId) {
      fetchTeam();
      fetchRoles();
    }
  }, [teamId]);

  useEffect(() => {
    if (teamId && activeTab === 'activity') {
      fetchActivityLogs();
    }
  }, [teamId, activeTab, activityPage]);

  const fetchTeam = async () => {
    if (!teamId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await getTeam(parseInt(teamId));
      setTeam(data);
      setEditForm({ teamName: data.teamName, description: data.description || '' });
    } catch (err: any) {
      console.error('Error fetching team:', err);
      setError(err.message || 'Failed to fetch team');
      toast.error(err.message || 'Failed to fetch team');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      setLoadingRoles(true);
      const roles = await getRoles();
      setAvailableRoles(roles);
    } catch (err: any) {
      console.error('Error fetching roles:', err);
      toast.error('Failed to fetch roles');
    } finally {
      setLoadingRoles(false);
    }
  };

  // Refresh team members table data with table-only loading
  const refreshTeamMembers = async () => {
    if (!teamId) return;
    try {
      setTableLoading(true);
      const data = await getTeam(parseInt(teamId));
      setTeam(data);
      setEditForm({ teamName: data.teamName, description: data.description || '' });
    } catch (err: any) {
      console.error('Error refreshing team members:', err);
      toast.error(err.message || 'Failed to refresh team members');
    } finally {
      setTableLoading(false);
    }
  };

  // Helper function to update team members locally without refetching
  const updateTeamMembers = (updater: (members: TeamMember[]) => TeamMember[]) => {
    setTeam((prevTeam) => {
      if (!prevTeam) return prevTeam;
      return {
        ...prevTeam,
        members: updater(prevTeam.members),
      };
    });
  };

  // Helper to add a new invitation to members list
  const addInvitationToMembers = (invitationData: {
    invitationId: number;
    email: string;
    roles: string[];
    invitationToken: string;
    expiresAt: string;
    userId?: number;
  }) => {
    const newMember: TeamMember = {
      memberId: null,
      userId: invitationData.userId || null,
      email: invitationData.email,
      firstname: null,
      lastname: null,
      roles: invitationData.roles,
      permissions: [],
      invitationStatus: 'pending',
      joinedAt: null,
      isActive: true,
      expiresAt: invitationData.expiresAt,
      invitationId: invitationData.invitationId,
      invitedAt: new Date().toISOString(),
      isPendingInvitation: true,
    };
    updateTeamMembers((members) => [...members, newMember]);
  };

  // Helper to update a member in the members list
  const updateMemberInList = (memberId: number | null, invitationId: number | undefined, updater: (member: TeamMember) => TeamMember) => {
    updateTeamMembers((members) =>
      members.map((member) => {
        if (memberId !== null && member.memberId === memberId) {
          return updater(member);
        }
        if (invitationId !== undefined && member.invitationId === invitationId) {
          return updater(member);
        }
        return member;
      })
    );
  };

  // Helper to remove a member from the members list
  const removeMemberFromList = (memberId: number | null, invitationId: number | undefined) => {
    updateTeamMembers((members) =>
      members.filter((member) => {
        if (memberId !== null && member.memberId === memberId) {
          return false;
        }
        if (invitationId !== undefined && member.invitationId === invitationId) {
          return false;
        }
        return true;
      })
    );
  };

  const handleUpdateTeam = async () => {
    if (!teamId || !isTeamOwner) return;
    try {
      await updateTeam(parseInt(teamId), editForm);
      toast.success('Team updated successfully');
      setEditing(false);
      fetchTeam();
    } catch (err: any) {
      toast.error(err.message || 'Failed to update team');
    }
  };

  const handleDeleteTeam = async () => {
    if (!teamId || !isTeamOwner) return;
    setDeletingTeam(true);
    try {
      await deleteTeam(parseInt(teamId));
      toast.success('Team deleted successfully');
      navigate('/teams');
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete team');
    } finally {
      setDeletingTeam(false);
    }
  };

  const handleInviteMember = async () => {
    if (!teamId || !canInviteMembers || !inviteForm.email || inviteForm.roles.length === 0) {
      if (!canInviteMembers) {
        toast.error('Only the team owner or members with "owner" role can invite members');
        return;
      }
      toast.error('Email and at least one role are required');
      return;
    }
    setInviting(true);
    try {
      await inviteTeamMember(parseInt(teamId), inviteForm);

      // Track member invited
      trackEvent(TEAM_EVENTS.MEMBER_INVITED, { team_id: teamId });

      // Track first invite as activation milestone
      const userId = auth.currentUser?.uid;
      if (userId) {
        trackActivationMilestone(ACTIVATION_EVENTS.FIRST_INVITE, userId);
      }

      toast.success('Invitation sent successfully');
      setShowInviteDialog(false);
      setInviteForm({ email: '', roles: [], message: '' });
      // Refresh team members table
      await refreshTeamMembers();
    } catch (err: any) {
      // Check if error is 428 (REACTIVATION_REQUIRED)
      if (err.status === 428 || err.code === 'REACTIVATION_REQUIRED') {
        // Show reactivation dialog instead of error toast
        setShowReactivationDialog(true);
      } else {
      toast.error(err.message || 'Failed to send invitation');
      }
    } finally {
      setInviting(false);
    }
  };

  const handleReactivateMember = async () => {
    if (!teamId || !inviteForm.email || !inviteForm.roles.length) {
      toast.error('Email and at least one role are required');
      return;
    }
    setReactivating(true);
    try {
      await reactivateTeamMember(parseInt(teamId), {
        email: inviteForm.email,
        roles: inviteForm.roles,
        message: inviteForm.message || 'Welcome back to the team!',
      });
      toast.success('Team member reactivated successfully');
      setShowReactivationDialog(false);
      setShowInviteDialog(false);
      setInviteForm({ email: '', roles: [], message: '' });
      // Refresh team members table
      await refreshTeamMembers();
    } catch (err: any) {
      // Handle different error status codes
      if (err.status === 404) {
        if (err.message?.includes('User not found')) {
          toast.error('User not found with this email address');
        } else if (err.message?.includes('not a member')) {
          toast.error('User is not a member of this team');
        } else {
          toast.error('Team not found');
        }
      } else if (err.status === 403) {
        toast.error('Insufficient permissions to reactivate team member');
      } else if (err.status === 400) {
        toast.error(err.message || 'Invalid request. Please check your input.');
      } else if (err.status === 409) {
        toast.error('User is already an active member of this team');
      } else if (err.status === 500) {
        toast.error('Failed to reactivate team member. Please try again.');
      } else {
        toast.error(err.message || 'Failed to reactivate team member');
      }
    } finally {
      setReactivating(false);
    }
  };

  const handleRemoveMember = async (member: TeamMember) => {
    if (!teamId || !canRemoveMembers || !team) {
      toast.error('Only the team owner or members with "owner" role can remove members');
      return;
    }
    
    // Double-check that user is the team owner OR has "owner" permission
    if (!isTeamOwner && !hasOwnerRole) {
      toast.error('Only the team owner or members with "owner" role can remove members');
      return;
    }
    
    // Check if this is a pending invitation or an accepted member
    const isPendingInvitation = member.invitationStatus === 'pending' || member.memberId === null;
    
    // For pending invitations, use invitationId
    if (isPendingInvitation) {
      if (!member.invitationId) {
        toast.error('Invalid invitation. Cannot revoke.');
        return;
      }
      
      if (!window.confirm('Are you sure you want to revoke this invitation?')) return;
      
      setRevokingInvitationId(member.invitationId);
      try {
        await revokeInvitation(member.invitationId);
        toast.success('Invitation revoked successfully');
        // Refresh team members table
        await refreshTeamMembers();
      } catch (err: any) {
        toast.error(err.message || 'Failed to revoke invitation');
      } finally {
        setRevokingInvitationId(null);
      }
      return;
    }
    
    // For accepted members, use memberId
    if (!member.memberId) {
      toast.error('Invalid member. Cannot remove.');
      return;
    }
    
    // Find the member to check if they are the owner
    if (member.userId === team.ownerId) {
      toast.error('Cannot remove the team owner');
      return;
    }
    
    if (!window.confirm('Are you sure you want to remove this member?')) return;
    
    setRemovingMemberId(member.memberId);
    try {
      await removeTeamMember(parseInt(teamId), member.memberId);
      toast.success('Member removed successfully');
      // Refresh team members table
      await refreshTeamMembers();
    } catch (err: any) {
      toast.error(err.message || 'Failed to remove member');
    } finally {
      setRemovingMemberId(null);
    }
  };

  const handleUpdateMemberRoles = async () => {
    if (!teamId || !selectedMember || memberRoles.length === 0) {
      toast.error('At least one role is required');
      return;
    }
    
    // Check if user has permission to manage roles (must be team owner OR team member with "owner" role)
    if (!canManageRoles) {
      toast.error('Only the team owner or members with "owner" role can manage roles');
      return;
    }
    
    // Prevent modifying owner's roles
    if (selectedMember.userId === team?.ownerId) {
      toast.error('Cannot modify the team owner\'s roles');
      return;
    }
    
    // Cannot update roles for pending invitations (they don't have memberId)
    if (!selectedMember.memberId || selectedMember.memberId === null) {
      toast.error('Cannot update roles for pending invitations');
      return;
    }
    
    try {
      setUpdatingRoles(true);
      await updateMemberRoles(parseInt(teamId), selectedMember.memberId, { roles: memberRoles });
      toast.success('Member roles updated successfully');
      setShowRoleDialog(false);
      setSelectedMember(null);
      // Refresh team members table
      await refreshTeamMembers();
    } catch (err: any) {
      toast.error(err.message || 'Failed to update member roles');
    } finally {
      setUpdatingRoles(false);
    }
  };

  const openRoleDialog = (member: TeamMember) => {
    // Check permission before opening dialog (must be team owner OR team member with "owner" role)
    if (!canManageRoles) {
      toast.error('Only the team owner or members with "owner" role can manage roles');
      return;
    }
    
    // Cannot manage roles for pending invitations (they don't have memberId)
    if (!member.memberId || member.memberId === null) {
      toast.error('Cannot manage roles for pending invitations');
      return;
    }
    
    // Prevent modifying owner's roles
    if (member.userId === team?.ownerId) {
      toast.error('Cannot modify the team owner\'s roles');
      return;
    }
    
    setSelectedMember(member);
    setMemberRoles([...member.roles]);
    setShowRoleDialog(true);
  };

  const fetchActivityLogs = async () => {
    if (!teamId) return;
    try {
      setActivityLoading(true);
      const response = await getTeamActivity(parseInt(teamId), {
        page: activityPage,
        limit: 50,
      });
      setActivityLogs(response.data);
      setActivityTotal(response.pagination.total);
    } catch (err: any) {
      console.error('Error fetching activity logs:', err);
      // Show the actual error message from the API (e.g., "insufficient permissions")
      const errorMessage = err?.message || 'Failed to fetch activity logs';
      toast.error(errorMessage);
    } finally {
      setActivityLoading(false);
    }
  };

  const handleViewPermissions = async (member: TeamMember) => {
    if (!teamId) return;
    
    // Cannot view permissions for pending invitations (they don't have memberId)
    // if (!member.memberId || member.memberId === null) {
    //   toast.error('Cannot view permissions for pending invitations');
    //   return;
    // }
    
    // Check if user can view permissions (own record or has team:manage)
    const isOwnRecord = member.userId === userProfile?.id;
    if (!canViewPermissions && !isOwnRecord) {
      toast.error('You do not have permission to view other members\' permissions');
      return;
    }
    
    // Store selected member to check if it's own record in dialog
    setSelectedMember(member);
    
    // Show dialog immediately with loading state
    setShowPermissionsDialog(true);
    setMemberPermissions(null); // Clear previous permissions
    
    try {
      setLoadingPermissions(true);
      // For pending invitations, pass invitationId as query parameter
      const permissions = await getMemberPermissions(
        parseInt(teamId), 
        member.memberId, 
        member.invitationId
      );
      setMemberPermissions(permissions);
    } catch (err: any) {
      console.error('Error fetching member permissions:', err);
      toast.error('Failed to fetch member permissions');
      // Close dialog on error
      setShowPermissionsDialog(false);
      setSelectedMember(null);
    } finally {
      setLoadingPermissions(false);
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const formatActionType = (actionType: string): string => {
    return actionType
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (loading) {
    return (
      <div className="bg-nova-dashboard-900 min-h-screen">
        <div className="p-4 sm:p-6 md:p-8">
          <div className="flex items-center justify-center mt-[5rem]">
            <div className="text-gray-600 font-family-messina-sans">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !team) {
    return (
      <div className="bg-nova-dashboard-900 min-h-screen">
        <div className="p-4 sm:p-6 md:p-8">
          <div className="flex items-center justify-center mt-[5rem]">
            <div className="text-red-600 font-family-messina-sans">{error || 'Team not found'}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-nova-dashboard-900 min-h-screen">
      <div className="p-3 sm:p-6 md:p-8">
        {/* Header */}
        <div className="flex flex-wrap items-center sm:items-center gap-3 mt-6 sm:mt-[3rem] mb-5 sm:mb-6">
          <button
            onClick={() => navigate('/teams')}
            className="p-2 sm:p-2 -ml-1 sm:ml-0 self-start sm:self-auto hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-700" />
          </button>
          {editing && canEditTeam ? (
            <div className="flex-1 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 min-w-0">
              <input
                type="text"
                value={editForm.teamName}
                onChange={(e) => setEditForm({ ...editForm, teamName: e.target.value })}
                className="text-[1.6rem] sm:text-[1.875rem] font-regular text-gray-900 font-family-messina-modern border-b border-gray-300 focus:outline-none focus:border-gray-900 pb-2 flex-1 min-w-0 w-full"
                placeholder="Team Name"
              />
              {canEditTeam && (
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <button
                    onClick={handleUpdateTeam}
                    className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-family-messina-sans flex items-center gap-2"
                  >
                    <Save size={16} />
                    <span>Save</span>
                  </button>
                  <button
                    onClick={() => {
                      setEditing(false);
                      setEditForm({ teamName: team.teamName, description: team.description || '' });
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans flex items-center gap-2"
                  >
                    <X size={16} />
                    <span>Cancel</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex-1 min-w-0 flex flex-wrap items-center gap-2 sm:gap-3">
              <h1 className="text-[1.6rem] sm:text-[1.875rem] font-regular text-gray-900 font-family-messina-modern break-words">
                {team.teamName}
              </h1>
              {/* Only show edit/delete buttons if user is the team owner (not team members) */}
              {/* Double-check: team must exist, userProfile must exist, and user must be owner */}
              {team && userProfile && isTeamOwner && (
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => setEditing(true)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans flex items-center gap-2"
                  >
                    <Edit size={16} />
                    <span>Edit</span>
                  </button>
                  <button
                    onClick={() => setShowDeleteDialog(true)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-family-messina-sans flex items-center gap-2"
                  >
                    <Trash2 size={16} />
                    <span>Delete</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Team Info */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
          {!editing && team.description && (
            <p className="text-sm text-gray-600 font-family-messina-sans mb-4">{team.description}</p>
          )}
          {editing && canEditTeam && (
            <textarea
              value={editForm.description}
              onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
              className="w-full text-sm text-gray-600 font-family-messina-sans border border-gray-300 rounded-lg p-3 focus:outline-none focus:border-gray-900 mb-4"
              placeholder="Team description"
              rows={3}
            />
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500 font-family-messina-sans">Artist:</span>
              <p className="text-gray-900 font-family-messina-sans font-medium">{team.artistName || 'N/A'}</p>
            </div>
            <div>
              <span className="text-gray-500 font-family-messina-sans">Owner:</span>
              <p className="text-gray-900 font-family-messina-sans font-medium">{team.ownerName || 'N/A'}</p>
            </div>
            <div>
              <span className="text-gray-500 font-family-messina-sans">Members:</span>
              <p className="text-gray-900 font-family-messina-sans font-medium">{acceptedMembersCount}</p>
            </div>
            <div>
              <span className="text-gray-500 font-family-messina-sans">Status:</span>
              <p className={`font-family-messina-sans font-medium ${
                team.isActive ? 'text-green-600' : 'text-gray-600'
              }`}>
                {team.isActive ? 'Active' : 'Inactive'}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <div className="flex overflow-x-auto">
              <button
                onClick={() => setActiveTab('members')}
                className={`px-6 py-3 text-sm font-medium font-family-messina-sans transition-colors ${
                  activeTab === 'members'
                    ? 'text-gray-900 border-b-2 border-gray-900'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Members ({isTeamOwner ? acceptedMembersCount : visibleMembers.filter(m => m.invitationStatus === 'accepted').length})
              </button>
              {canViewActivityLog && (
                <button
                  onClick={() => setActiveTab('activity')}
                  className={`px-6 py-3 text-sm font-medium font-family-messina-sans transition-colors ${
                    activeTab === 'activity'
                      ? 'text-gray-900 border-b-2 border-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <History size={16} />
                    <span>Activity Log</span>
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Members Section */}
        {activeTab === 'members' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
              <h2 className="text-lg font-semibold text-gray-900 font-family-messina-modern">
                Team Members ({isTeamOwner ? acceptedMembersCount : visibleMembers.filter(m => m.invitationStatus === 'accepted').length})
              </h2>
              {/* Only team owner can invite members */}
              {canInviteMembers && (
                <button
                  onClick={() => setShowInviteDialog(true)}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-family-messina-sans flex items-center gap-2"
                >
                  <UserPlus size={16} />
                  <span>Invite Member</span>
                </button>
              )}
            </div>

          {visibleMembers.length === 0 ? (
            <div className="text-center py-8 text-gray-500 font-family-messina-sans">
              {team.members.length === 0 
                ? 'No members yet. Invite someone to get started.'
                : 'No members visible to you.'}
            </div>
          ) : (
            <div className="relative overflow-x-auto">
              {tableLoading && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 size={24} className="animate-spin text-gray-600" />
                    <span className="text-sm text-gray-600 font-family-messina-sans">Loading...</span>
                  </div>
                </div>
              )}
              <table className="w-full border-collapse">
                    <thead>
                  <tr className="border-b border-gray-300">
                    <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                      Member
                    </th>
                    {canViewEmails && (
                      <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                        Email
                      </th>
                    )}
                    <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                      Roles
                    </th>
                    <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                      Status
                    </th>
                    {(canViewPermissions || canManageRoles || canRemoveMembers) && (
                      <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                        More
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {visibleMembers.map((member) => {
                    const isOwnRecord = isCurrentUserMember(member);
                    // Check if this is the logged-in user for remove icon visibility (explicit number comparison)
                    const isLoggedInUser = member.userId !== null && member.userId !== undefined && 
                                          userProfile?.id !== null && userProfile?.id !== undefined &&
                                          Number(member.userId) === Number(userProfile?.id);
                    
                    // Check if logged-in user is a team member with "owner" role
                    // This is true if: user is in team members list AND has "owner" role AND is not the team owner
                    const isLoggedInUserTeamMemberWithOwnerRole = isCurrentUserTeamMember && hasOwnerRole && !isTeamOwner;
                    
                    // Check if we should hide remove icon for this member
                    // Case 1: User has team permissions but is not team owner and doesn't have "owner" role - hide for owner and logged-in user
                    // Case 2: If logged-in user is a team member with "owner" role, hide remove icon for themselves
                    const shouldHideRemoveIcon = 
                      (hasAccess && !isTeamOwner && !hasOwnerRole && (member.userId === team.ownerId || isLoggedInUser)) ||
                      (isLoggedInUserTeamMemberWithOwnerRole && isLoggedInUser);
                    
                    // Debug logging for team members with owner role
                    if (isLoggedInUser && hasOwnerRole && !isTeamOwner) {
                      console.log('[TeamDetails] Remove icon visibility check:', {
                        memberName: member.firstname || member.email,
                        memberUserId: member.userId,
                        memberUserIdType: typeof member.userId,
                        userProfileId: userProfile?.id,
                        userProfileIdType: typeof userProfile?.id,
                        isLoggedInUser,
                        isCurrentUserTeamMember,
                        hasOwnerRole,
                        isTeamOwner,
                        isLoggedInUserTeamMemberWithOwnerRole,
                        shouldHideRemoveIcon,
                        canRemoveMembers,
                        memberRoles: member.roles,
                        currentUserMemberRoles: currentUserMember?.roles
                      });
                    }
                    
                    return (
                      <tr key={member.memberId} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-[0.875rem] text-gray-900 font-family-messina-sans font-medium">
                              {member.firstname && member.lastname
                                ? `${member.firstname} ${member.lastname}`
                                : 'Member'}
                            </p>
                          </div>
                        </td>
                        {canViewEmails && (
                          <td className="py-3 px-4">
                            <p className="text-[0.875rem] text-gray-600 font-family-messina-sans">
                              {member.email || '-'}
                            </p>
                          </td>
                        )}
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1">
                            {member.roles.map((role) => (
                              <span
                                key={role}
                                className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-family-messina-sans"
                              >
                                {role}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {(() => {
                            // Check if pending invitation is expired
                            const isExpired = member.invitationStatus === 'pending' && isInvitationExpired(member);
                            const displayStatus = isExpired ? 'expired' : member.invitationStatus;
                            
                            return (
                              <span
                                className={`px-2 py-1 rounded text-xs font-family-messina-sans ${
                                  displayStatus === 'accepted'
                                    ? 'bg-green-100 text-green-800'
                                    : displayStatus === 'pending'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : displayStatus === 'expired'
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-gray-100 text-gray-800'
                                }`}
                              >
                                {displayStatus}
                              </span>
                            );
                          })()}
                        </td>
                        {(canViewPermissions || canManageRoles || canRemoveMembers) && (
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              {/* View Permissions - Only for own record or if has team:manage */}
                              {(canViewPermissions || isOwnRecord) && (
                                <button
                                  onClick={() => handleViewPermissions(member)}
                                  className="p-1 text-gray-700 hover:text-gray-900 transition-colors"
                                  title={isOwnRecord ? "View Your Permissions" : "View Permissions"}
                                >
                                  <Eye size={16} />
                                </button>
                              )}
                              {/* Manage Roles - Team owner OR team member with "owner" role can manage roles */}
                              {/* Hide manage roles icon for team owner and team members with "owner" role when viewing their own record */}
                              {/* Hide manage roles icon for rejected and revoked invitations */}
                              {canManageRoles && 
                               member.userId !== team.ownerId && 
                               !shouldHideRemoveIcon &&
                               member.invitationStatus !== 'rejected' &&
                               member.invitationStatus !== 'revoked' && (
                                <button
                                  onClick={() => openRoleDialog(member)}
                                  className="p-1 text-gray-700 hover:text-gray-900 transition-colors"
                                  title="Manage Roles"
                                >
                                  <Shield size={16} />
                                </button>
                              )}
                              {/* Remove Member - Only team owner can remove members at any invitation status, but not the owner themselves */}
                              {/* If user has team permissions but is not team owner and doesn't have "owner" role, hide remove icon for owner and logged-in user */}
                              {/* Also hide remove icon for team members with "owner" role when viewing their own record */}
                              {/* Hide remove icon for rejected and revoked invitations */}
                              {canRemoveMembers && 
                               member.userId !== team.ownerId && 
                               // Hide remove icon if shouldHideRemoveIcon is true
                               !shouldHideRemoveIcon &&
                               member.invitationStatus !== 'rejected' &&
                               member.invitationStatus !== 'revoked' && (
                                <button
                                  onClick={() => handleRemoveMember(member)}
                                  disabled={
                                    (member.memberId !== null && removingMemberId === member.memberId) ||
                                    (member.invitationId !== undefined && revokingInvitationId === member.invitationId)
                                  }
                                  className="p-1 text-red-600 hover:text-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  title={
                                    (member.memberId !== null && removingMemberId === member.memberId) ||
                                    (member.invitationId !== undefined && revokingInvitationId === member.invitationId)
                                      ? (member.invitationStatus === 'pending' ? "Revoking invitation..." : "Removing member...")
                                      : (member.invitationStatus === 'pending' ? "Revoke Invitation" : "Remove Member")
                                  }
                                >
                                  {(member.memberId !== null && removingMemberId === member.memberId) ||
                                   (member.invitationId !== undefined && revokingInvitationId === member.invitationId) ? (
                                    <Loader2 size={16} className="animate-spin" />
                                  ) : (
                                    <X size={16} />
                                  )}
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}

        {/* Activity Log Section - Only visible to team:manage users */}
        {activeTab === 'activity' && canViewActivityLog && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900 font-family-messina-modern">
                Activity Log
              </h2>
            </div>

            {activityLoading ? (
              <div className="text-center py-8 text-gray-500 font-family-messina-sans">
                Loading activity logs...
              </div>
            ) : activityLogs.length === 0 ? (
              <div className="text-center py-8 text-gray-500 font-family-messina-sans">
                No activity logs found
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-gray-300">
                        <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                          Date
                        </th>
                        <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                          Action
                        </th>
                        <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                          User
                        </th>
                        <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                          Target
                        </th>
                        <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                          Details
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {activityLogs.map((log) => (
                        <tr key={log.activityId} className="border-b border-gray-200 hover:bg-gray-50">
                          <td className="py-3 px-4 text-sm text-gray-600 font-family-messina-sans">
                            {formatDate(log.createdAt)}
                          </td>
                          <td className="py-3 px-4">
                            <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-family-messina-sans">
                              {formatActionType(log.actionType)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-900 font-family-messina-sans">
                            {log.userName || 'N/A'}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-900 font-family-messina-sans">
                            {log.targetUserName || '-'}
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600 font-family-messina-sans">
                            <ActivityDetails log={log} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {activityTotal > 50 && (
                  <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
                    <div className="text-sm text-gray-600 font-family-messina-sans">
                      Showing page {activityPage} of {Math.ceil(activityTotal / 50)}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setActivityPage((p) => Math.max(1, p - 1))}
                        disabled={activityPage === 1}
                        className="px-3 py-1 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        <ChevronLeft size={16} />
                        Previous
                      </button>
                      <button
                        onClick={() => setActivityPage((p) => p + 1)}
                        disabled={activityPage >= Math.ceil(activityTotal / 50)}
                        className="px-3 py-1 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        Next
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Invite Member Dialog */}
      {showInviteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 font-family-messina-modern">
                Invite Team Member
              </h3>
              <button
                onClick={() => {
                  setShowInviteDialog(false);
                  setInviteForm({ email: '', roles: [], message: '' });
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-family-messina-sans">
                  Email
                </label>
                <input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-900 font-family-messina-sans"
                  placeholder="member@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-family-messina-sans">
                  Roles
                </label>
                {loadingRoles ? (
                  <div className="text-sm text-gray-500">Loading roles...</div>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2">
                    {rolesForInvite.map((role) => (
                      <label
                        key={role.roleId}
                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={inviteForm.roles.includes(role.roleCode)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setInviteForm({
                                ...inviteForm,
                                roles: [...inviteForm.roles, role.roleCode],
                              });
                            } else {
                              setInviteForm({
                                ...inviteForm,
                                roles: inviteForm.roles.filter((r) => r !== role.roleCode),
                              });
                            }
                          }}
                          className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                        />
                        <div>
                          <span className="text-sm text-gray-900 font-family-messina-sans">
                            {role.roleName}
                          </span>
                          {role.description && (
                            <p className="text-xs text-gray-500 font-family-messina-sans">
                              {role.description}
                            </p>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-family-messina-sans">
                  Message (Optional)
                </label>
                <textarea
                  value={inviteForm.message}
                  onChange={(e) => setInviteForm({ ...inviteForm, message: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-900 font-family-messina-sans"
                  placeholder="Add a personal message..."
                  rows={3}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowInviteDialog(false);
                  setInviteForm({ email: '', roles: [], message: '' });
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans"
              >
                Cancel
              </button>
              <button
                onClick={handleInviteMember}
                disabled={inviting}
                className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-family-messina-sans flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {inviting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <span>Send Invitation</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reactivation Dialog */}
      {showReactivationDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 font-family-messina-modern">
                Reactivate Team Member
              </h3>
              <button
                onClick={() => {
                  setShowReactivationDialog(false);
                }}
                className="text-gray-500 hover:text-gray-700"
                disabled={reactivating}
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-800 font-family-messina-sans mb-2">
                  <strong>This user is already registered for this team but is inactive.</strong>
                </p>
                <p className="text-sm text-yellow-700 font-family-messina-sans">
                  Would you like to reactivate <strong>{inviteForm.email}</strong> and send them a new invitation?
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-family-messina-sans">
                  Email
                </label>
                <input
                  type="email"
                  value={inviteForm.email}
                  disabled
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-600 font-family-messina-sans"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-family-messina-sans">
                  Roles
                </label>
                {loadingRoles ? (
                  <div className="text-sm text-gray-500">Loading roles...</div>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-2">
                    {rolesForInvite.map((role) => (
                      <label
                        key={role.roleId}
                        className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={inviteForm.roles.includes(role.roleCode)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setInviteForm({
                                ...inviteForm,
                                roles: [...inviteForm.roles, role.roleCode],
                              });
                            } else {
                              setInviteForm({
                                ...inviteForm,
                                roles: inviteForm.roles.filter((r) => r !== role.roleCode),
                              });
                            }
                          }}
                          disabled={reactivating}
                          className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900"
                        />
                        <div>
                          <span className="text-sm text-gray-900 font-family-messina-sans">
                            {role.roleName}
                          </span>
                          {role.description && (
                            <p className="text-xs text-gray-500 font-family-messina-sans">
                              {role.description}
                            </p>
                          )}
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-family-messina-sans">
                  Message (Optional)
                </label>
                <textarea
                  value={inviteForm.message}
                  onChange={(e) => setInviteForm({ ...inviteForm, message: e.target.value })}
                  disabled={reactivating}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-gray-900 font-family-messina-sans"
                  placeholder="Welcome back to the team!"
                  rows={3}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowReactivationDialog(false);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans"
                disabled={reactivating}
              >
                Cancel
              </button>
              <button
                onClick={handleReactivateMember}
                disabled={reactivating || inviteForm.roles.length === 0}
                className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-family-messina-sans disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {reactivating ? 'Reactivating...' : 'Reactivate & Send Invitation'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Management Dialog */}
      {showRoleDialog && selectedMember && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 font-family-messina-modern">
                Manage Roles for {selectedMember.email}
              </h3>
              <button
                onClick={() => {
                  if (!updatingRoles) {
                    setShowRoleDialog(false);
                    setSelectedMember(null);
                  }
                }}
                disabled={updatingRoles}
                className="text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-2 mb-4">
              {availableRoles.map((role) => (
                <label
                  key={role.roleId}
                  className={`flex items-center gap-2 p-2 rounded ${
                    updatingRoles ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-50 cursor-pointer'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={memberRoles.includes(role.roleCode)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setMemberRoles([...memberRoles, role.roleCode]);
                      } else {
                        setMemberRoles(memberRoles.filter((r) => r !== role.roleCode));
                      }
                    }}
                    disabled={updatingRoles}
                    className="w-4 h-4 text-gray-900 border-gray-300 rounded focus:ring-gray-900 disabled:cursor-not-allowed"
                  />
                  <div>
                    <span className="text-sm text-gray-900 font-family-messina-sans">
                      {role.roleName}
                    </span>
                    {role.description && (
                      <p className="text-xs text-gray-500 font-family-messina-sans">
                        {role.description}
                      </p>
                    )}
                  </div>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowRoleDialog(false);
                  setSelectedMember(null);
                }}
                disabled={updatingRoles}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateMemberRoles}
                disabled={updatingRoles}
                className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-family-messina-sans disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {updatingRoles ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Updating...</span>
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permissions Dialog */}
      {showPermissionsDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 shadow-xl max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 font-family-messina-modern">
                {selectedMember && isCurrentUserMember(selectedMember) 
                  ? 'Your Permissions' 
                  : 'Member Permissions'}
              </h3>
              <button
                onClick={() => {
                  setShowPermissionsDialog(false);
                  setMemberPermissions(null);
                  setSelectedMember(null);
                }}
                className="text-gray-500 hover:text-gray-700"
                disabled={loadingPermissions}
              >
                <X size={20} />
              </button>
            </div>
            
            {loadingPermissions ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-4"></div>
                <div className="text-gray-500 font-family-messina-sans">
                  Loading permissions...
                </div>
              </div>
            ) : memberPermissions ? (
              <div className="space-y-6">
                {/* Roles Section */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-2 font-family-messina-sans">
                    Roles
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {memberPermissions.roles.map((role) => (
                      <span
                        key={role}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm font-family-messina-sans"
                      >
                        {role}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Permissions by Category */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 font-family-messina-sans">
                    Permissions by Category
                  </h4>
                  <div className="space-y-4">
                    {Object.entries(memberPermissions.permissionsByCategory).map(
                      ([category, permissions]) => (
                        permissions.length > 0 && (
                          <div key={category} className="border border-gray-200 rounded-lg p-4">
                            <h5 className="text-sm font-medium text-gray-700 mb-2 font-family-messina-sans capitalize">
                              {category}
                            </h5>
                            <div className="flex flex-wrap gap-2">
                              {permissions.map((permission) => {
                                const permDetail = memberPermissions.permissions.find(
                                  (p) => p.code === permission
                                );
                                return (
                                  <div
                                    key={permission}
                                    className="px-2 py-1 bg-green-50 text-green-700 rounded text-xs font-family-messina-sans"
                                    title={permDetail?.name || permission}
                                  >
                                    {permission}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )
                      )
                    )}
                  </div>
                </div>

                {/* All Permissions List */}
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 font-family-messina-sans">
                    All Permissions
                  </h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto border border-gray-200 rounded-lg p-3">
                    {memberPermissions.permissions.map((permission) => (
                      <div
                        key={permission.code}
                        className="flex justify-between items-start py-2 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900 font-family-messina-sans">
                            {permission.name}
                          </div>
                          <div className="text-xs text-gray-500 font-family-messina-sans">
                            {permission.code}
                          </div>
                          {permission.category && (
                            <div className="text-xs text-gray-400 font-family-messina-sans mt-1">
                              Category: {permission.category}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 font-family-messina-sans">
                No permissions data available
              </div>
            )}

            {!loadingPermissions && memberPermissions && (
              <div className="flex justify-end mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowPermissionsDialog(false);
                    setMemberPermissions(null);
                    setSelectedMember(null);
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 font-family-messina-modern">
              Delete Team
            </h3>
            <p className="text-sm text-gray-600 mb-6 font-family-messina-sans">
              Are you sure you want to delete this team? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteDialog(false)}
                disabled={deletingTeam}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTeam}
                disabled={deletingTeam}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-family-messina-sans flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deletingTeam ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <span>Delete</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamDetails;

